import { useSyncExternalStore } from 'react'

// ─── Dismissed alerts store ───────────────────────────────────────────────────
//
// Compliance alerts come from the product API via the bridge and have no
// server-side "acknowledge" endpoint. "Acknowledge" is therefore a local,
// per-browser action: the alert id is remembered in localStorage and filtered
// out of the command centre until it stops being reported. A tiny module-level
// store (read via useSyncExternalStore) keeps every component — Quick Actions,
// Needs Attention, the sidebar badge — in sync without prop drilling.

const KEY = 'tmagent:dismissed-alerts'
const EMPTY: string[] = []

let cache: string[] = load()
const listeners = new Set<() => void>()

function load(): string[] {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function persist(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(cache))
  } catch {
    /* storage unavailable — keep in-memory only */
  }
}

/** Mark one or more alert ids as acknowledged. No-op if nothing changes. */
export function dismissAlerts(ids: string[]): void {
  if (ids.length === 0) return
  const set = new Set(cache)
  let changed = false
  for (const id of ids) {
    if (!set.has(id)) {
      set.add(id)
      changed = true
    }
  }
  if (!changed) return
  cache = Array.from(set)
  persist()
  for (const l of listeners) l()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function getSnapshot(): string[] {
  return cache
}

function getServerSnapshot(): string[] {
  return EMPTY
}

export interface DismissedAlerts {
  dismissed: string[]
  isDismissed: (id: string) => boolean
  dismiss: (ids: string[]) => void
}

export function useDismissedAlerts(): DismissedAlerts {
  const dismissed = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return {
    dismissed,
    isDismissed: (id: string) => dismissed.includes(id),
    dismiss: dismissAlerts,
  }
}
